import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Grid,
  GridItem,
  Flex,
  Icon,
  Input,
  Select,
} from "@chakra-ui/react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import moment from "moment";
import { createStandaloneToast } from "@chakra-ui/react";
import { IDS } from "../../constants/Ids";
import { CallbackRequestModalProps } from "../../modals/PropInterface/CallbackRequestModalProps";
import { BookingSlot, TimeSlot } from "../../modals/TaskPayload";

const toast = createStandaloneToast();


export const CallbackRequestModal: React.FC<CallbackRequestModalProps> = ({
  isOpen,
  onClose,
  onRequestCallback,
  ibsAdonisService,
  estimateDetails,
  showHideLoader = () => {},
  currentUser,
  availability = [],
  allCallBackTasks = []
}) => {
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>("");

  // Generate calendar days
  const generateCalendarDays = () => {
    const startOfMonth = currentMonth.clone().startOf("month");
    const endOfMonth = currentMonth.clone().endOf("month");
    const startDate = startOfMonth.clone().startOf("week");
    const endDate = endOfMonth.clone().endOf("week");

    const days = [];
    let day = startDate.clone();

    while (day.isSameOrBefore(endDate)) {
      days.push(day.clone());
      day.add(1, "day");
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  const getBookingSlotForDay = (day: moment.Moment): BookingSlot | null => {
    const dayName = day.format('dddd'); // Gets full day name (Monday, Tuesday, etc.)
    return availability.find(slot => slot.day === dayName) || null;
  };

  const generateTimeSlots = (
    bookingSlot: BookingSlot | null,
    allCallBackTasks: any[] = [],
    selectedDate?: moment.Moment
  ): TimeSlot[] => {
    if (!bookingSlot || bookingSlot.is_active === 0) {
      return [];
    }

    const slots: TimeSlot[] = [];
    const startTime = moment(bookingSlot.start_time, "hh:mm A");
    const endTime = moment(bookingSlot.end_time, "hh:mm A");

    let currentTime = startTime.clone();

    // Booked slots for filteration
    const bookedSlotsForSelectedDate = allCallBackTasks
      .filter(task => {
        const taskDate = moment(task.dueDate);
        return selectedDate ? taskDate.isSame(selectedDate, 'day') : false;
      })
      .map(task => moment(task.dueDate).format("hh:mm A"));

    while (currentTime.isBefore(endTime)) {
      const timeSlot = currentTime.format("hh:mm A");

      slots.push({
        slot: timeSlot,
        isAvailable: !bookedSlotsForSelectedDate.includes(timeSlot)
      });

      currentTime.add(60, "minutes");
    }

    return slots;
  };

  const getAvailableTimeSlots = (): TimeSlot[] => {
    if (!selectedDate) return [];
    
    const bookingSlot = getBookingSlotForDay(selectedDate);
    return generateTimeSlots(bookingSlot, allCallBackTasks, selectedDate);
  };

  const handleDateClick = (day: moment.Moment) => {
    // Only allow future dates and days with active booking slots
    if (day.isAfter(moment(), "day")) {
      const bookingSlot = getBookingSlotForDay(day);
      if (bookingSlot && bookingSlot.is_active === 1) {
        setSelectedDate(day);
        setSelectedTime(""); // Reset time when date changes
      }
    }
  };

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const handleSubmit = async () => {
    if (!selectedDate) {
      toast({
        title: "Date Required",
        description: "Please select a date for your callback request.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!selectedTime) {
      toast({
        title: "Time Required",
        description: "Please select a time for your callback request.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    showHideLoader(true);
    
    try {
      setIsLoading(true)
      // Create callback request task
      const taskListId = IDS.TASK_LIST.CALLBACK_REQUEST;
      
      const taskObj = {
        Title: `${estimateDetails?.estimateNumber || "N/A"} - Callback Request`,
        assignments: [
          {
            id: IDS.TASK_LIST.CALLBACK_REQUEST,
            type: "TaskList",
            userName: "Call Backs/Reworks",
            isDeleted: false,
          },
          {
            id: estimateDetails?.estimateId || "",
            userName: estimateDetails?.estimateNumber?.toString() || "N/A",
            type: "Estimate",
            lastStarted: new Date(),
            isDeleted: false,
          }
        ],
        AssignDate: (() => {
          const timeOnlyMoment = moment(selectedTime, 'hh:mm A');

          const assignDate = selectedDate.clone()
            .hour(timeOnlyMoment.hour())
            .minute(timeOnlyMoment.minute())
            .second(0)
            .millisecond(0);

          return assignDate.format("YYYY-MM-DDTHH:mm"); 
        })(),
        taskItemRecurrence: null,
        additionalNote: `Callback Request\n----------------------------\nRequested Date: ${selectedDate.format("DD/MM/YYYY")}\nRequested Time: ${selectedTime}\nJob Number: ${estimateDetails?.jobNumber || "N/A"}\nEstimate Id: ${estimateDetails?.estimateId || "N/A"}\nRequested By: ${currentUser?.name || "Customer"}`,
        checklistItems: [
          {
            caption: "Contact customer",
            isChecked: false,
            id: "",
            isDeleted: false,
            isRenamed: false,
          },
          {
            caption: "Update customer",
            isChecked: false,
            id: "",
            isDeleted: false,
            isRenamed: false,
          }
        ],
      };

      // Add estimator if available
      if (estimateDetails?.estimatorId) {
        taskObj.assignments.push({
          id: estimateDetails.estimatorId,
          userName: estimateDetails.estimatorName || "Estimator",
          type: "User",
          lastStarted: new Date(),
          isDeleted: false,
        });
      }
      
      const res: any = await ibsAdonisService.addTask(taskListId, taskObj);
            
      if (res?.success || res?.IsSuccess) {
        const newTask = {
          id: res?.data?.id || res?.Data?.id || Date.now().toString(),
          title: taskObj.Title,
          dueDate: taskObj.AssignDate,
          created: new Date().toISOString(),
          assignments: taskObj.assignments,
          additionalNote: taskObj.additionalNote,
          checklistItems: taskObj.checklistItems,
        };
        toast({
          title: "Callback Request Submitted",
          description: "We have received your callback request and will contact you at the specified time.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onRequestCallback(selectedDate.format("YYYY-MM-DD"), selectedTime, newTask);
        onClose();
      } else {
        toast({
          title: "Request Failed",
          description: "Unable to submit your callback request. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Request Failed",
        description: "Something went wrong. Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      showHideLoader(false);
      setIsLoading(false)
    }
  };

  const isDateSelectable = (day: moment.Moment) => {
    if (!day.isAfter(moment(), "day")) return false;
    
    const bookingSlot = getBookingSlotForDay(day);
    return bookingSlot && bookingSlot.is_active === 1;
  };

  const isDateSelected = (day: moment.Moment) => {
    return selectedDate && day.isSame(selectedDate, "day");
  };

  const isToday = (day: moment.Moment) => {
    return day.isSame(moment(), "day");
  };

  const getDayColor = (selected: boolean, isCurrentMonth: boolean, selectable: boolean) => {
    if (selected) return "white";
    if (!isCurrentMonth) return "gray.300";
    if (selectable) return "gray.700";
    return "gray.400";
  };

  const availableTimeSlots = getAvailableTimeSlots();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" closeOnOverlayClick={true}>
      <ModalOverlay />
      <ModalContent borderRadius="12px" maxW="320px" alignSelf={"center"}>
        <ModalBody pb={4} pt={4}>
          {/* Calendar Section */}
          <Box mb={4}>
            {/* Calendar Header */}
            <Flex justify="space-between" align="center" mb={3}>
              <Icon
                as={FaChevronLeft}
                cursor="pointer"
                onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, "month"))}
                color="gray.500"
                _hover={{ color: "blue.500" }}
                size="sm"
              />
              <Text fontSize="md" fontWeight="semibold">
                {currentMonth.format("MMMM YYYY")}
              </Text>
              <Icon
                as={FaChevronRight}
                cursor="pointer"
                onClick={() => setCurrentMonth(currentMonth.clone().add(1, "month"))}
                color="gray.500"
                _hover={{ color: "blue.500" }}
                size="sm"
              />
            </Flex>

            {/* Days of Week */}
            <Grid templateColumns="repeat(7, 1fr)" gap={1} mb={2}>
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <GridItem key={day} textAlign="center">
                  <Text fontSize="xs" fontWeight="medium" color="gray.600">
                    {day}
                  </Text>
                </GridItem>
              ))}
            </Grid>

            {/* Calendar Days */}
            <Grid templateColumns="repeat(7, 1fr)" gap={1}>
              {calendarDays.map((day, index) => {
                const isCurrentMonth = day.month() === currentMonth.month();
                const selectable = isDateSelectable(day);
                const selected = isDateSelected(day);
                const today = isToday(day);

                return (
                  <GridItem key={index}>
                    <Box
                      h="32px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="6px"
                      cursor={selectable ? "pointer" : "default"}
                      bg={selected ? "blue.500" : today ? "blue.100" : "transparent"}
                      color={getDayColor(!!selected, isCurrentMonth, selectable || false)}
                      border={today && !selected ? "2px solid" : "none"}
                      borderColor="blue.500"
                      _hover={
                        selectable
                          ? {
                              bg: selected ? "blue.600" : "blue.50",
                            }
                          : {}
                      }
                      onClick={() => handleDateClick(day)}
                    >
                      <Text fontSize="xs" fontWeight={selected ? "bold" : "normal"}>
                        {day.date()}
                      </Text>
                    </Box>
                  </GridItem>
                );
              })}
            </Grid>
          </Box>

          {/* Divider Line */}
          <Box borderTop="1px solid" borderColor="gray.200" mb={4} />

          {/* Time Selector Section */}
          <Box>
            {selectedDate && availableTimeSlots.length > 0 ? (
              <Grid templateColumns="repeat(3, 1fr)" gap={2}>
                {availableTimeSlots.map((slotData) => (
                  <Button
                    key={slotData?.slot}
                    size="sm"
                    variant={selectedTime === slotData?.slot ? "solid" : "ghost"}
                    colorScheme={selectedTime === slotData?.slot ? "blue" : "gray"}
                    onClick={() => setSelectedTime(slotData?.slot)}
                    borderRadius="8px"
                    fontSize="xs"
                    h="32px"
                    border={selectedTime === slotData?.slot ? "1px solid" : "none"}
                    borderColor="blue.500"
                    disabled={!slotData?.isAvailable}
                  >
                    {slotData?.slot}
                  </Button>
                ))}
              </Grid>
            ) : selectedDate ? (
              <Text textAlign="center" color="gray.500" fontSize="sm">
                No available slots for this day
              </Text>
            ) : (
              <Text textAlign="center" color="gray.500" fontSize="sm">
                Select a date to view available time slots
              </Text>
            )}
          </Box>

          {/* Submit Button */}
          <Button
            colorScheme="blue"
            size="sm"
            w="100%"
            mt={4}
            onClick={handleSubmit}
            isDisabled={!selectedDate || !selectedTime || isLoading}
          >
          
            <span>
              Request Call Back
            </span>
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}; 